import { Category } from "@/dto/category";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { OrderType } from "@/constants/enum";
import { giftCardCategory } from "@/utils/giftCardUtils";
import pom from "@/utils/pom";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import { applyDataTestId } from "@/utils/testUtils";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Dispatch, SetStateAction, useMemo } from "react";
import { MobileDayPartMenuList } from "./dayPartMenuSelector";
import DefaultLogoIcon from "@/components/_icons/defaultLogoIcon";
import { LanguageSelector } from "@/components/languageSelector/languageSelector";
import Divider from "@/components/divider/divider";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import {
  FEATURE_FLAG_LOCALIZATION_ON_COLO2,
  ROUTE_MENU_ALL,
  ROUTE_SERVICES_ALL,
  ROUTE_SHOP,
  ROUTE_UNCATEGORISED,
  UNCATEGORISED_CATEGORY_ID,
} from "@/utils/constants";

export interface MobileNavigationBarProps {
  sideMenuOpen: boolean;
  setSideMenuOpen: Dispatch<SetStateAction<boolean>>;
  categories: Category[];
  scanToOrderInfo?: ScanToOrderInfo;
  merchant: Merchant;
  dayPartMenus?: DayPart[];
  selectedMenu?: DayPart | null;
  // eslint-disable-next-line no-unused-vars
  onDayPartMenuChange?: (menu: DayPart) => void;
}

export const MobileNavigationBar = ({
  sideMenuOpen,
  setSideMenuOpen,
  categories,
  scanToOrderInfo,
  merchant,
  dayPartMenus,
  selectedMenu = null,
  onDayPartMenuChange,
}: MobileNavigationBarProps) => {
  const t = useTranslations("MobileNavigationBar");
  const localizationEnabledFlag = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_LOCALIZATION_ON_COLO2
  );
  const {
    isService,
    isRestaurant,
    merchantMeta,
    isHybrid,
    isSellingGiftCards,
    logo,
  } = merchant;
  const searchParams = useSearchParams();
  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  const fileteredDayPartMenus: DayPart[] = useMemo(() => {
    if (!isDineIn) return dayPartMenus ?? [];

    if (isDineIn && selectedMenu) return [selectedMenu];

    return [];
  }, [isDineIn, dayPartMenus, selectedMenu]);

  const renderMenuContent = () => {
    if (isService || (isRestaurant && dayPartMenus?.length == 0)) {
      return (
        <>
          {isService && !merchant.hasOnlyRetailItems && (
            <Link
              href={ROUTE_SERVICES_ALL}
              className={["text-lg", "text-[#2E2E2E]"].join(" ")}
              onClick={() => setSideMenuOpen(false)}
            >
              {t("services")}
            </Link>
          )}
          {(isHybrid || (isService && merchant?.hasOnlyRetailItems)) && (
            <Link
              href={ROUTE_SHOP}
              className={["text-lg", "text-[#2E2E2E]"].join(" ")}
              onClick={() => setSideMenuOpen(false)}
            >
              {t("shop")}
            </Link>
          )}
          {isRestaurant && (
            <Link
              href={ROUTE_MENU_ALL}
              className={["text-lg", "text-[#2E2E2E]"].join(" ")}
              onClick={() => setSideMenuOpen(false)}
            >
              {t("menu")}
            </Link>
          )}
        </>
      );
    }

    if (dayPartMenus && dayPartMenus?.length > 0) {
      return (
        <MobileDayPartMenuList
          dayPartMenus={fileteredDayPartMenus}
          merchant={merchant}
          onMenuSelect={(menu) => {
            setSideMenuOpen(false);
            onDayPartMenuChange?.(menu);
          }}
          selectedMenu={selectedMenu}
        />
      );
    }

    return categories
      .filter((c: Category) => c.id !== giftCardCategory.id)
      .map((c: { id: string; name: string }) => {
        let queryParams = "";
        if (scanToOrderInfo) {
          queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
        }
        const restaurantUrl = queryParams
          ? `${ROUTE_MENU_ALL}/${queryParams}#${encodeURIComponent(c.name)}`
          : `${ROUTE_MENU_ALL}#${encodeURIComponent(c.name)}`;

        const getHref = () => {
          if (isService) {
            return `${ROUTE_SERVICES_ALL}#${encodeURIComponent(c.name)}`;
          } else if (isRestaurant) {
            return restaurantUrl;
          } else {
            const name =
              c.id === UNCATEGORISED_CATEGORY_ID ? ROUTE_UNCATEGORISED : c.name;
            return `/${encodeURIComponent(name)}`;
          }
        };

        return (
          <Link
            key={c.id}
            href={getHref()}
            className={["flex", "text-lg", "text-[#2E2E2E]"].join(" ")}
            onClick={() => setSideMenuOpen(false)}
          >
            {c.name}
          </Link>
        );
      });
  };

  return (
    <div>
      <Dialog
        data-testid={applyDataTestId(
          pom.mobileNavigationBar.mobileNavBarContainer
        )}
        open={sideMenuOpen}
        onClose={setSideMenuOpen}
        className="relative py-10 z-50"
      >
        <DialogBackdrop
          data-testid={applyDataTestId(
            pom.mobileNavigationBar.mobileNavBarBackdrop
          )}
          transition
          className="fixed inset-0 bg-[#25333880] transition-opacity duration-300 ease-in-out data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 flex w-[84%]">
              <DialogPanel
                transition
                className="pointer-events-auto relative w-screen max-w-md transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
              >
                <TransitionChild>
                  <div
                    data-testid={applyDataTestId(
                      pom.mobileNavigationBar.mobileNavBarCloseButton
                    )}
                    className="absolute right-[27px] top-[21px] z-10 -ml-8 flex duration-300 ease-in-out data-[closed]:opacity-0"
                  >
                    <button
                      type="button"
                      onClick={() => setSideMenuOpen(false)}
                      className="relative rounded-md text-black"
                    >
                      <span className="absolute -inset-2.5" />
                      <span className="sr-only">{t("closePanel")}</span>
                      <XMarkIcon aria-hidden="true" className="size-6" />
                    </button>
                  </div>
                </TransitionChild>
                <div className="relative flex h-full flex-col overflow-y-auto pb-10 pt-10 px-5 bg-white shadow-xl">
                  <DialogTitle>
                    <Link href={"/"} onClick={() => setSideMenuOpen(false)}>
                      {logo ? (
                        <Image
                          data-testid={applyDataTestId(
                            pom.header.merchantLogoDataTestId
                          )}
                          src={logo}
                          width={51}
                          height={51}
                          alt={pom.header.merchantLogo}
                        />
                      ) : (
                        <DefaultLogoIcon
                          data-testid={applyDataTestId(
                            pom.header.merchantLogoDataTestId
                          )}
                          className="w-[51px] h-[51px]"
                          aria-label={pom.header.merchantLogo}
                        />
                      )}
                    </Link>
                  </DialogTitle>
                  <div
                    className="relative mt-6 flex flex-col gap-6 flex-1"
                    data-testid={applyDataTestId(
                      pom.mobileNavigationBar.mobileMenuLinksContainer
                    )}
                  >
                    {renderMenuContent()}
                    {isSellingGiftCards && !scanToOrderInfo && (
                      <Link
                        href="/giftcard"
                        className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                        onClick={() => setSideMenuOpen(false)}
                      >
                        {t("giftCard")}
                      </Link>
                    )}
                    {merchantMeta?.selectedSetup !== "Simple" && (
                      <Link
                        href="/about"
                        className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                        onClick={() => setSideMenuOpen(false)}
                      >
                        {t("about")}
                      </Link>
                    )}

                    <Divider classNames="w-[calc(100%-80px)] !block border-[#979797] !border-t" />

                    <LanguageSelector
                      localizationEnabledFlag={localizationEnabledFlag}
                    />
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
